"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MuTxtApi = void 0;
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const behavior_1 = require("../behavior");
const typeToLabel_1 = require("../util/typeToLabel");
const util_1 = require("../util");
/** Public API for of the mu-txt editor. */
class MuTxtApi {
    constructor(state) {
        this.state = state;
    }
    get editor() {
        return this.state.editor;
    }
    // ------------------------------------------------------------------- Cursor
    focused() {
        const editor = this.state.editor;
        return slate_react_1.ReactEditor.isFocused(editor);
    }
    focus() {
        if (!this.focused())
            slate_react_1.ReactEditor.focus(this.state.editor);
    }
    blur() {
        if (this.focused())
            slate_react_1.ReactEditor.blur(this.state.editor);
    }
    deselect() {
        try {
            slate_react_1.ReactEditor.deselect(this.editor);
        }
        catch { }
        try {
            window.getSelection?.()?.removeAllRanges();
        }
        catch { }
    }
    hasSelection() {
        const { selection } = this.state.editor;
        return !!selection && !slate_1.Range.isCollapsed(selection);
    }
    focusRect() {
        if (!this.state.cursor.value)
            return;
        const selection = window.getSelection();
        if (!selection?.focusNode)
            return;
        try {
            const focusRange = document.createRange();
            focusRange.setStart(selection.focusNode, selection.focusOffset);
            focusRange.collapse(true);
            const rect = focusRange.getClientRects()[0] ?? focusRange.getBoundingClientRect();
            if (rect.width > 0 || rect.height > 0)
                return rect;
        }
        catch { }
        return;
    }
    focusPoint() {
        try {
            const rect = this.focusRect();
            if (!rect)
                return;
            const x = rect.left + rect.width / 2;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const GAP = 8;
            if (spaceBelow >= spaceAbove) {
                return { x, y: rect.bottom + GAP, dx: 0, dy: 1 };
            }
            return { x, y: rect.top - GAP, dx: 0, dy: -1 };
        }
        catch {
            return;
        }
    }
    /**
     * Move the user's attention to a specific block. Smooth-scrolls the block
     * into view.
     */
    navigateTo(path) {
        const editor = this.editor;
        let node;
        try {
            node = slate_1.Node.get(editor, path);
        }
        catch {
            return false;
        }
        if (!slate_1.Element.isElement(node))
            return false;
        this.deselect();
        this.blur();
        try {
            const domNode = slate_react_1.ReactEditor.toDOMNode(editor, node);
            domNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => {
                this.deselect();
                this.blur();
                domNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 36);
        }
        catch { }
        return true;
    }
    // ------------------------------------------------------------------- Inline
    marks() {
        return slate_1.Editor.marks(this.editor);
    }
    isMarkActive(format) {
        const marks = slate_1.Editor.marks(this.editor);
        return marks ? marks[format] === true : false;
    }
    toggleMark(format) {
        const editor = this.editor;
        if (this.isMarkActive(format))
            slate_1.Editor.removeMark(editor, format);
        else
            slate_1.Editor.addMark(editor, format, true);
    }
    eraseMarks() {
        (0, behavior_1.eraseMarks)(this.editor);
    }
    // -------------------------------------------------------------------- Block
    block(at) {
        const { editor } = this;
        if (!at) {
            const { selection } = editor;
            if (selection)
                at = slate_1.Editor.unhangRange(editor, selection);
        }
        if (at) {
            const [match] = slate_1.Editor.nodes(editor, {
                at,
                match: (node) => slate_1.Element.isElement(node) && slate_1.Editor.isBlock(editor, node),
            });
            if (match)
                return match[0];
        }
        const firstChild = editor.children[0];
        return slate_1.Element.isElement(firstChild) && slate_1.Editor.isBlock(editor, firstChild)
            ? firstChild
            : null;
    }
    blockAbove(predicate, at, mode = 'lowest') {
        const { editor } = this;
        if (!at) {
            const { selection } = editor;
            if (selection)
                at = slate_1.Editor.unhangRange(editor, selection);
        }
        if (!at)
            return;
        return slate_1.Editor.above(this.editor, {
            at,
            match: (node) => slate_1.Element.isElement(node) &&
                slate_1.Editor.isBlock(this.editor, node) &&
                (predicate?.(node) ?? true),
            mode,
        });
    }
    listAbove(predicate, at, mode = 'lowest') {
        return this.blockAbove((n) => (0, behavior_1.isListType)(n.type) && (predicate?.(n) ?? true), at, mode);
    }
    blockLabel(at) {
        const element = this.block(at);
        if (!element)
            return 'Paragraph';
        return (0, typeToLabel_1.typeToLabel)(element.type) || 'Paragraph';
    }
    // ----------------------------------------------------------------- Document
    isEmpty() {
        return (0, util_1.isEmptyDoc)(this.editor);
    }
    toBinary() {
        return this.state.obj.api.model.toBinary();
    }
}
exports.MuTxtApi = MuTxtApi;
